import { Divider, DividerColor } from '@eliberty/design-system-react';

import { SELECTOR_MODE } from '~constants';
import {
  DateSelector,
  ParticipantSelector,
  ProductCategorySelector,
  SelectedContractor,
  ValidityCategorySelector,
} from '~organisms';
import {
  ConsumerCategory,
  Contractor,
  Product,
  ProductCategory,
  ProductCategoryValidity,
  Timeslot,
} from '~types/appsync.types';
import { ConsumerCategoryConfirmationState } from '~types/participant';
import type { ProductCategoryOption } from '~types/productCategory';

type ProductSelectorsProps = {
  productCategory?: ProductCategory;
  /**
   * Validities to display in the selector.
   * In slug-product mode, contains only the single validity from the targeted product.
   * In normal mode, contains all validities from productCategory.
   */
  filteredValidities?: ProductCategoryValidity[];
  date: any;
  selectedTimeslot?: Timeslot;
  validityCategory: any;
  product: any;
  products?: Product[];
  participants: any[];
  orderItems?: any[];
  currency?: string;
  participantsLimit?: number;
  isSimulationOnError: boolean;
  consumerCategories: ConsumerCategory[];
  participantMode?: SELECTOR_MODE;
  isLoadingProductCategory?: boolean;
  isLoadingPrices?: boolean;
  onChangeValidityCategory: (validityCategory: any) => void;
  onChangeDate: (date: any) => void;
  onChangeParticipants: (participants: any[]) => void;
  onTimeslotSelected: (slot: Timeslot | undefined) => void;
  isDateDisabled?: boolean;
  isParticipantsDisabled?: boolean;
  onConsumerCategoryConfirmationChange?: (state: ConsumerCategoryConfirmationState | null) => void;
  onDynamicDiscountChange?: (hasDiscount: boolean) => void;
  productCategories?: ProductCategoryOption[];
  selectedProductCategoryShortname?: string;
  onProductCategoryChange?: (shortname: string) => void;
  isLoadingProductCategories?: boolean;
  hasProductCategoriesError?: boolean;
  hasProductCategoryError?: boolean;
  selectedContractor?: Contractor;
};

export const ProductSelectors = ({
  productCategory,
  filteredValidities,
  date,
  selectedTimeslot,
  validityCategory,
  product,
  products,
  participants,
  orderItems,
  currency,
  participantsLimit,
  isSimulationOnError,
  consumerCategories,
  participantMode,
  isLoadingProductCategory = false,
  isLoadingPrices = false,
  isDateDisabled = false,
  isParticipantsDisabled = false,
  onChangeValidityCategory,
  onChangeDate,
  onChangeParticipants,
  onTimeslotSelected,
  onConsumerCategoryConfirmationChange,
  onDynamicDiscountChange,
  productCategories,
  selectedProductCategoryShortname,
  onProductCategoryChange,
  isLoadingProductCategories = false,
  hasProductCategoriesError = false,
  hasProductCategoryError = false,
  selectedContractor,
}: ProductSelectorsProps) => {
  const showProductCategoryStep = !!productCategories && !!onProductCategoryChange;

  return (
    <>
      {selectedContractor && (
        <>
          <SelectedContractor contractor={selectedContractor} />
          <Divider color={DividerColor.MediumGrey} />
        </>
      )}
      {showProductCategoryStep && (
        <>
          <ProductCategorySelector
            productCategories={productCategories}
            selectedProductCategory={selectedProductCategoryShortname}
            onProductCategoryChange={onProductCategoryChange}
            isLoading={isLoadingProductCategories}
            hasError={hasProductCategoriesError}
          />
          <Divider color={DividerColor.MediumGrey} />
        </>
      )}
      <ValidityCategorySelector
        validityCategories={filteredValidities ?? productCategory?.validities}
        onValidityCategoryClick={onChangeValidityCategory}
        selectedProduct={product}
        products={products}
        selectedValidityCategory={validityCategory}
        isLoadingProductCategory={isLoadingProductCategory}
        disabled={showProductCategoryStep && !selectedProductCategoryShortname}
        hasProductCategoryError={hasProductCategoryError}
      />
      <Divider color={DividerColor.MediumGrey} />
      <DateSelector
        product={product}
        validityCategoryValue={validityCategory?.value}
        selectedDate={date}
        selectedTimeslot={selectedTimeslot}
        disabled={isDateDisabled}
        currency={currency}
        onDateChange={onChangeDate}
        onTimeslotSelected={onTimeslotSelected}
        participantsCount={participants?.length ?? 0}
        includeDates={product?.bookingDays}
        onDynamicDiscountChange={onDynamicDiscountChange}
      />
      <Divider color={DividerColor.MediumGrey} />
      <ParticipantSelector
        participants={participants}
        orderItems={orderItems}
        currency={currency}
        onChangeParticipants={onChangeParticipants}
        consumerCategories={consumerCategories}
        isSimulationOnError={isSimulationOnError}
        participantsLimit={participantsLimit}
        participantMode={participantMode}
        isLoading={isLoadingPrices}
        disabled={isParticipantsDisabled}
        onConsumerCategoryConfirmationChange={onConsumerCategoryConfirmationChange}
      />
    </>
  );
};
